import { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Link, Typography } from '@mui/material';
import licenseComplianceService from '../../services/licenseComplianceService';
import type {
  LicenseFulfillmentDto,
  MultiLicenseFulfillmentDto,
} from '../../services/licenseComplianceService';
import { getApiErrorInfo } from '../../services/api';
import LicenseFulfillmentPanel from './LicenseFulfillmentPanel';
import { markCelebratedKeys, planCelebrations, readCelebratedKeys } from './celebration';
import DisclaimerNote from './DisclaimerNote';
import { FULFILLMENT_DISCLAIMER } from './disclaimers';

interface LicenseRequirementsSectionProps {
  /**
   * Bump to refetch fulfillment after credits change elsewhere on the page
   * (e.g. an external credit was added), so totals update without a page
   * refresh. The refetch is silent — no loading spinner, data swaps in place.
   */
  refreshToken?: number;
  /**
   * Fires with the set-up licenses whenever fulfillment loads, so the parent
   * page can derive per-license reporting-period presets without a second
   * fetch. On a *first-load* failure it fires with [] (the parent falls back
   * to generic presets); refetch failures after data exists stay silent so
   * already-rendered presets never disappear.
   */
  onLicensesLoaded?: (licenses: LicenseFulfillmentDto[]) => void;
}

/**
 * Lists each of the user's licenses with its CPE requirements and progress.
 * Shown on the Reporting page; licenses and requirements themselves are
 * managed on the License Management page (/app/licenses).
 */
export default function LicenseRequirementsSection({
  refreshToken = 0,
  onLicensesLoaded,
}: LicenseRequirementsSectionProps) {
  const [fulfillment, setFulfillment] = useState<MultiLicenseFulfillmentDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Achievements first observed this session, kept in state so the UI persists
  // across refetches while localStorage prevents re-firing on future visits.
  const [celebrateLicenseIds, setCelebrateLicenseIds] = useState<ReadonlySet<string>>(new Set());
  const [celebrateSubGoals, setCelebrateSubGoals] = useState<ReadonlyMap<string, ReadonlySet<string>>>(
    new Map()
  );

  // Monotonic sequence so an out-of-order response from an older request can
  // never overwrite fresher data (rapid credit actions, StrictMode dev double-effect).
  const requestSeqRef = useRef(0);
  // Tracks whether any fulfillment data ever arrived, so only a *first-load*
  // failure reports an empty license list to the parent.
  const hasLoadedRef = useRef(false);

  const loadData = useCallback(async () => {
    const seq = ++requestSeqRef.current;
    setLoadError(null);
    try {
      const data = await licenseComplianceService.getFulfillment();
      if (seq !== requestSeqRef.current) return;
      const plan = planCelebrations(data.licenses, readCelebratedKeys());
      markCelebratedKeys(plan.keysToMark);
      if (plan.licenseIds.length > 0) {
        setCelebrateLicenseIds((prev) => new Set([...prev, ...plan.licenseIds]));
      }
      if (plan.subGoalIds.size > 0) {
        setCelebrateSubGoals((prev) => {
          const next = new Map(prev);
          for (const [licenseId, subIds] of plan.subGoalIds) {
            next.set(licenseId, new Set([...(prev.get(licenseId) ?? []), ...subIds]));
          }
          return next;
        });
      }
      setFulfillment(data);
      hasLoadedRef.current = true;
      onLicensesLoaded?.(data.licenses);
    } catch (error) {
      if (seq !== requestSeqRef.current) return;
      setLoadError(getApiErrorInfo(error).message);
      if (!hasLoadedRef.current) onLicensesLoaded?.([]);
    } finally {
      if (seq === requestSeqRef.current) setLoading(false);
    }
  }, [onLicensesLoaded]);

  useEffect(() => {
    void loadData();
  }, [loadData, refreshToken]);

  const licenses = fulfillment?.licenses ?? [];

  // Classify-your-credits nudge: caps can only apply to credits with a known
  // delivery method, so unclassified external credits silently bypass every
  // limit. Shown once for the whole section when any limited license has them.
  const nudgeLicense = licenses.find(
    (l) => (l.creditLimits?.length ?? 0) > 0 && (l.unclassifiedThirdPartyCreditCount ?? 0) > 0
  );

  // Display name of the primary license a derived (reciprocity) entry mirrors —
  // the panel only receives one license, so the lookup happens here. For
  // eligible-but-not-elected entries the same name feeds the discovery hint
  // (the gate guarantees a set-up primary, so it's always in this list).
  const reciprocitySourceName = (license: LicenseFulfillmentDto): string | null => {
    const source = license.reciprocitySourceLicenseId
      ? licenses.find((l) => l.licenseId === license.reciprocitySourceLicenseId)
      : license.reciprocityEligible
        ? licenses.find((l) => l.isPrimary)
        : null;
    return source ? source.stateName || source.stateCode : null;
  };

  return (
    <Box sx={{ mb: 3 }}>
      {/* Section header: h5 + hairline rule so the header visually owns the
          content below it instead of competing with it as body-sized text */}
      <Box
        sx={{
          mb: 2.5,
          pb: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="h5">License requirements</Typography>
        <Button component={RouterLink} to="/app/licenses" variant="outlined" size="small">
          Manage licenses
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : loadError && fulfillment === null ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => { setLoading(true); void loadData(); }}>
              Retry
            </Button>
          }
        >
          {loadError}
        </Alert>
      ) : licenses.length === 0 ? (
        <Alert severity="info">
          No licenses are set up for requirement tracking yet.{' '}
          <Link component={RouterLink} to="/app/licenses">
            Manage your licenses
          </Link>{' '}
          to get started.
        </Alert>
      ) : (
        <>
          {/* Refetch failed but earlier data exists: keep the panels, warn inline. */}
          {loadError && (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={() => void loadData()}>
                  Retry
                </Button>
              }
            >
              Couldn't refresh requirement totals. Showing the last loaded data.
            </Alert>
          )}
          {nudgeLicense && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Some of your external credits don't have a delivery method, so credit caps
              can't apply to them.{' '}
              <Link component={RouterLink} to="/app/cpe-history">
                Set a delivery method
              </Link>{' '}
              to keep your totals accurate.
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {licenses.map((license) => (
              <LicenseFulfillmentPanel
                key={license.licenseId}
                license={license}
                reciprocitySourceName={reciprocitySourceName(license)}
                interactive
                onChanged={loadData}
                justAchieved={license.isCompliant && celebrateLicenseIds.has(license.licenseId)}
                achievedSubIds={celebrateSubGoals.get(license.licenseId)}
              />
            ))}
          </Box>

          {/* Fine print after the content it qualifies (mirrors the admin
              Compliance Report footer) so the panels lead the section. The FAQ
              link lives here because it's the same register: meta-commentary
              about how tracking works. */}
          <DisclaimerNote sx={{ mt: 2 }}>
            {FULFILLMENT_DISCLAIMER} Questions about how tracking works? See the{' '}
            <Link component={RouterLink} to="/app/faq#compliance-tracking">
              compliance tracking FAQ
            </Link>
            .
          </DisclaimerNote>
        </>
      )}
    </Box>
  );
}
